/**
 * Hirschberg's LCS algorithm: O(m*n) time, O(min(m,n)) space.
 * Returns matching index pairs (i in xs, j in ys).
 *
 * Ported from `core/lcs/Hirschberg.kt` in adf-content-reconciler.
 *
 * `maxRegions` is a safety cutoff: if the LCS would produce more than this many
 * distinct match runs (heuristically: the number of returned pairs is an upper
 * bound on the number of regions), we bail out and return `null` so the caller
 * can fall back to emitting a single Replace over the whole range. This protects
 * against pathological diffs on large inputs where surgical steps offer no real
 * benefit.
 */
export function hirschberg<T>(
	xs: readonly T[],
	ys: readonly T[],
	equal: (a: T, b: T) => boolean,
	maxRegions = 0,
): Array<[number, number]> | null {
	return hirschbergView(xs, 0, xs.length, ys, 0, ys.length, equal, maxRegions);
}

function hirschbergView<T>(
	xs: readonly T[],
	xLo: number,
	xHi: number,
	ys: readonly T[],
	yLo: number,
	yHi: number,
	equal: (a: T, b: T) => boolean,
	maxRegions: number,
): Array<[number, number]> | null {
	const xLen = xHi - xLo;
	const yLen = yHi - yLo;
	if (xLen === 0 || yLen === 0) return [];

	if (xLen === 1) {
		for (let j = 0; j < yLen; j++) {
			if (equal(xs[xLo], ys[yLo + j])) return [[xLo, yLo + j]];
		}
		return [];
	}
	if (yLen === 1) {
		for (let i = 0; i < xLen; i++) {
			if (equal(xs[xLo + i], ys[yLo])) return [[xLo + i, yLo]];
		}
		return [];
	}

	const midX = xLo + (xLen >> 1);
	const l1 = lcsRow(xs, xLo, midX, ys, yLo, yHi, equal, /*reverse*/ false);
	const l2 = lcsRow(xs, midX, xHi, ys, yLo, yHi, equal, /*reverse*/ true);

	// Find optimal split point in Y (relative offset into ys[yLo..yHi]).
	let bestK = 0;
	let bestSum = -1;
	for (let k = 0; k <= yLen; k++) {
		const sum = l1[k] + l2[k];
		if (sum > bestSum) {
			bestSum = sum;
			bestK = k;
		}
	}
	const split = yLo + bestK;

	const left = hirschbergView(xs, xLo, midX, ys, yLo, split, equal, maxRegions);
	if (left === null) return null;
	if (maxRegions > 0 && left.length > maxRegions) return null;

	const right = hirschbergView(xs, midX, xHi, ys, split, yHi, equal, maxRegions);
	if (right === null) return null;
	if (maxRegions > 0 && left.length + right.length > maxRegions) return null;

	// Indices in `right` are already absolute (we passed absolute xs/ys plus bounds).
	return left.concat(right);
}

/**
 * One row of the LCS DP. When `reverse` is true, iterates from the high end
 * backwards, which produces the suffix-LCS lengths needed by the Hirschberg split.
 */
function lcsRow<T>(
	xs: readonly T[],
	xLo: number,
	xHi: number,
	ys: readonly T[],
	yLo: number,
	yHi: number,
	equal: (a: T, b: T) => boolean,
	reverse: boolean,
): number[] {
	const yLen = yHi - yLo;
	const dp = new Array<number>(yLen + 1).fill(0);

	const xStart = reverse ? xHi - 1 : xLo;
	const xEnd = reverse ? xLo - 1 : xHi;
	const xStep = reverse ? -1 : 1;

	for (let i = xStart; i !== xEnd; i += xStep) {
		let prev = 0;
		const yStart = reverse ? yLen - 1 : 0;
		const yEnd = reverse ? -1 : yLen;
		const yStep = reverse ? -1 : 1;

		for (let j = yStart; j !== yEnd; j += yStep) {
			// Forward: dp[j+1] = match? prev+1 : max(dp[j+1], dp[j])
			// Reverse: dp[j]   = match? prev+1 : max(dp[j],   dp[j+1])
			const cur = reverse ? j : j + 1;
			const other = reverse ? j + 1 : j;
			const temp = dp[cur];
			dp[cur] = equal(xs[i], ys[yLo + j]) ? prev + 1 : Math.max(dp[cur], dp[other]);
			prev = temp;
		}
	}
	return dp;
}
